import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileCode2, FlaskConical, Save, Send, Trash2, Truck, XCircle } from "lucide-react";
import { DeleteOrderDialog, canDelete } from "@/components/DeleteOrderDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, x12Date, fmtNum, ts, ediName } from "@/lib/api";
import { PageHeader, Panel, Th, Td, btn, field } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { EdiImportDialog } from "@/components/EdiImportDialog";
import { RawEdiDialog } from "@/components/RawEdiDialog";
import { ViewPoButton } from "@/components/ViewPoButton";
import { OrderIdocPanel } from "@/components/sap/IdocPanel";

const Lbl = ({ children }) => <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">{children}</div>;

const Pick = ({ value, onChange, options, testId, disabled }) => (
  <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className={`h-9 ${field}`} data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
    <SelectContent className="border-slate-700 bg-[#252A35] text-slate-100">
      {options.map(([v, l]) => (
        <SelectItem key={v} value={v} data-testid={`${testId}-opt-${v}`}>{l}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const Party = ({ code, p }) => (
  <div>
    <Lbl>{code === "BT" ? "Bill to" : code === "ST" ? "Ship to" : code}</Lbl>
    <div className="text-sm text-slate-100">{p.name}</div>
    <div className="text-xs text-slate-400">{[...(p.address || []), [p.city, p.state, p.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
    {p.code && <div className="font-mono text-[10px] text-slate-500">ID {p.code}</div>}
  </div>
);

const Header = ({ o }) => (
  <Panel className="grid gap-5 p-5 sm:grid-cols-2 lg:grid-cols-4" testId="order-header-panel">
    {Object.entries(o.parties || {}).map(([k, p]) => <Party key={k} code={k} p={p} />)}
    <div className="space-y-2 text-xs">
      <div><Lbl>PO date / required</Lbl><span className="text-slate-200">{x12Date(o.po_date)} → {x12Date(o.delivery_date)}</span></div>
      <div><Lbl>Terms</Lbl><span className="text-slate-200">{o.terms || "—"}</span></div>
    </div>
    <div className="space-y-2 text-xs">
      <div><Lbl>References</Lbl>{Object.entries(o.refs || {}).map(([k, v]) => <div key={k} className="font-mono text-slate-300">{k}: {v}</div>)}</div>
      {o.total_amount && <div><Lbl>AMT TT</Lbl><span className="font-mono text-slate-200">{o.total_amount}</span></div>}
    </div>
  </Panel>
);

const Lines = ({ o, items, edit, map, setMap, ship, setShip }) => (
  <Panel title="Line items" testId="order-lines-panel">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-slate-800"><tr><Th>#</Th><Th>Buyer IDs</Th><Th>Description</Th><Th>EP part (item master)</Th><Th>Qty</Th><Th>UOM</Th><Th>Price</Th><Th>Avail @ {o.site_code || "site"}</Th><Th>Shipped</Th>{o.order_type === "replenishment" && <Th>Received</Th>}{ship && <Th>Ship qty</Th>}</tr></thead>
        <tbody className="divide-y divide-slate-800/70">
          {o.lines.map((l) => {
            const short = ["shipped", "short_shipped"].includes(o.status) && l.shipped_qty < l.qty;
            return (
              <tr key={l.line_no} data-testid={`order-line-${l.line_no}`}>
                <Td className="font-mono">{l.line_no}</Td>
                <Td className="font-mono text-[11px]">{Object.entries(l.ids || {}).map(([k, v]) => <div key={k}>{k} {v}</div>)}</Td>
                <Td className="max-w-[240px]">{l.description || "—"}</Td>
                <Td className="min-w-[180px]">
                  {edit ? (
                    <Pick value={map[l.line_no]} onChange={(v) => setMap({ ...map, [l.line_no]: v })} options={items.map((i) => [i.part_no, `${i.part_no} · ${i.type}`])} testId={`line-map-${l.line_no}`} />
                  ) : l.matched ? (
                    <span className="font-mono text-emerald-300">{l.part_no}</span>
                  ) : (
                    <StatusBadge status="unmatched" label="Not mapped" />
                  )}
                </Td>
                <Td className="font-mono text-slate-100">{fmtNum(l.qty)}</Td>
                <Td className="font-mono">{l.uom}</Td>
                <Td className="font-mono">{l.price || "—"}</Td>
                <Td className={`font-mono ${o.order_type === "customer" && (l.available ?? 0) < l.qty ? "text-amber-300" : ""}`} data-testid={`line-available-${l.line_no}`}>{fmtNum(l.available ?? 0)}</Td>
                <Td className={`font-mono ${short ? "text-rose-300" : ""}`} data-testid={`line-shipped-${l.line_no}`}>{fmtNum(l.shipped_qty)}</Td>
                {o.order_type === "replenishment" && <Td className="font-mono" data-testid={`line-received-${l.line_no}`}>{fmtNum(l.received_qty)}</Td>}
                {ship && (
                  <Td><Input type="number" value={ship[l.line_no] ?? ""} onChange={(e) => setShip({ ...ship, [l.line_no]: e.target.value })} className={`h-8 w-28 ${field}`} data-testid={`ship-qty-${l.line_no}`} /></Td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Panel>
);

const F = ({ label, children }) => <div><Lbl>{label}</Lbl>{children}</div>;

const FulfilmentForm = ({ f, setF, sites, isCust }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
      <F label="Order type"><Pick value={f.order_type} onChange={set("order_type")} options={[["customer", "Customer (940 to Group O)"], ["replenishment", "Replenishment (ship to xDock)"]]} testId="fulfil-order-type" /></F>
      <F label={isCust ? "Ship from xDock" : "Destination xDock"}><Pick value={f.site_code} onChange={set("site_code")} options={sites.filter((s) => s.code !== "EP").map((s) => [s.code, `${s.code} · ${s.city}`])} testId="fulfil-site" /></F>
      {isCust && (
        <>
          <F label="Carrier SCAC (W6610)"><Input value={f.carrier_scac || ""} onChange={set("carrier_scac")} className={field} data-testid="fulfil-scac" placeholder="e.g. AMZF" /></F>
          <F label="Method (W6602)"><Pick value={f.ship_method} onChange={set("ship_method")} options={[["T", "T · Motor common carrier"], ["L", "L · LTL"], ["R", "R · Rail"], ["S", "S · Small parcel"]]} testId="fulfil-method" /></F>
          <F label="Freight payment (W6601)"><Pick value={f.payment_method} onChange={set("payment_method")} options={[["CC", "CC · Collect"], ["PP", "PP · Prepaid"], ["PU", "PU · Pickup"], ["TP", "TP · Third party"]]} testId="fulfil-payment" /></F>
          <F label="Requested ship date"><Input type="date" value={f.ship_date || ""} onChange={set("ship_date")} className={field} data-testid="fulfil-ship-date" /></F>
          <F label="BOL # (N9 BM)"><Input value={f.bol || ""} onChange={set("bol")} className={field} data-testid="fulfil-bol" /></F>
          <F label="Load # (N9 LO)"><Input value={f.load_no || ""} onChange={set("load_no")} className={field} data-testid="fulfil-load" /></F>
          <F label="Routing (W6605)"><Input value={f.routing || ""} onChange={set("routing")} className={field} data-testid="fulfil-routing" /></F>
          <div className="sm:col-span-2 lg:col-span-3"><F label="Warehouse instructions (NTE)"><Textarea value={f.notes || ""} onChange={set("notes")} className={`h-10 min-h-0 ${field}`} data-testid="fulfil-notes" /></F></div>
        </>
      )}
    </div>
  );
};

const Shipments = ({ o }) =>
  o.shipments?.length > 0 && (
    <Panel title="945 Warehouse Shipping Advice - shipment confirmations" testId="order-shipments-panel">
      {o.shipments.map((s, i) => (
        <div key={i} className="border-b border-slate-800 p-5 last:border-0">
          <div className="mb-3 flex flex-wrap gap-6 font-mono text-xs text-slate-400">
            <span>Shipment <b className="text-slate-100">{s.shipment_id}</b></span>
            <span>BOL <b className="text-slate-100">{s.bol || "—"}</b></span>
            <span>PRO <b className="text-slate-100">{s.pro || "—"}</b></span>
            <span>Ship date <b className="text-slate-100">{x12Date(s.ship_date)}</b></span>
            <span>Pallets <b className="text-slate-100">{s.pallets.length}</b></span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {s.pallets.map((p, j) => (
              <span key={j} className="rounded-sm border border-slate-800 bg-[#17191F] px-2 py-1 font-mono text-[10px] text-slate-300">{p.sscc || "—"} · {p.part} × {p.qty}</span>
            ))}
          </div>
        </div>
      ))}
    </Panel>
  );

const History = ({ o, onView }) => (
  <Panel title="Audit trail & EDI" testId="order-history-panel">
    <div className="flex flex-wrap gap-2 border-b border-slate-800 px-5 py-3">
      {(o.edi_ids || []).map((id, i) => (
        <Button key={id} size="sm" variant="ghost" onClick={() => onView(id)} className={btn.ghost} data-testid={`order-edi-${i}`}>
          <FileCode2 className="mr-1 h-3.5 w-3.5" /> EDI #{i + 1}
        </Button>
      ))}
    </div>
    <ol className="space-y-3 p-5">
      {[...o.history].reverse().map((h, i) => (
        <li key={i} className="flex gap-3 text-xs">
          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
          <div>
            <div className="text-slate-200">{h.action} {h.note && <span className="text-slate-400">— {h.note}</span>}</div>
            <div className="font-mono text-[10px] text-slate-500">{ts(h.at)} · {h.by}</div>
          </div>
        </li>
      ))}
    </ol>
  </Panel>
);

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [del, setDel] = useState(false);
  const [o, setO] = useState(null);
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [f, setF] = useState({});
  const [map, setMap] = useState({});
  const [ship, setShip] = useState({});
  const [shipMeta, setShipMeta] = useState({ bol: "", ship_date: "" });
  const [imp, setImp] = useState({ open: false, raw: "" });
  const [view, setView] = useState(null);

  const hydrate = (d) => {
    setO(d);
    setF({ ship_method: "T", payment_method: "", ...d.fulfilment, order_type: d.order_type, site_code: d.site_code });
    setMap(Object.fromEntries(d.lines.map((l) => [l.line_no, l.part_no])));
    setShip(Object.fromEntries(d.lines.map((l) => [l.line_no, l.qty])));
  };
  const load = useCallback(() => api.get(`/orders/${id}`).then((r) => hydrate(r.data)), [id]);
  useEffect(() => {
    load();
    api.get("/items").then((r) => setItems(r.data));
    api.get("/sites").then((r) => setSites(r.data));
  }, [load]);

  const run = async (fn, msg) => {
    try {
      const r = await fn();
      if (r?.data?.id) hydrate(r.data);
      toast.success(msg);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const save = () => run(() => api.put(`/orders/${id}`, { ...f, lines: Object.entries(map).filter(([, v]) => v).map(([line_no, part_no]) => ({ line_no, part_no })) }), "Order saved");
  const submit = async () => (await save()) && run(() => api.post(`/orders/${id}/release`), "940 Warehouse Shipping Order released");
  const shipNow = async () => (await save()) && run(() => api.post(`/orders/${id}/ship`, { ...shipMeta, lines: Object.entries(ship).map(([line_no, qty]) => ({ line_no, qty: Number(qty) || 0 })) }), "Marked as shipped to Group O");
  const simulate = async () => {
    const r = await api.get(`/orders/${id}/test-response`);
    setImp({ open: true, raw: r.data.raw });
  };

  if (!o) return <div className="text-sm text-slate-500">Loading…</div>;
  const delOpen = del && o;
  const isCust = f.order_type === "customer";
  const editable = o.status === "received";

  return (
    <div className="space-y-6">
      <Link to={o.order_type === "customer" ? "/orders" : "/replenishment"} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="order-back-link">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Link>
      <PageHeader title={`PO ${o.po_number}`} subtitle={`Depositor order ${o.depositor_order_no} · ${o.source} · site ${o.site_code || "unset"}`}>
        <StatusBadge status={o.order_type} testId="order-type-badge" />
        <StatusBadge status={o.status} testId="order-status-badge" />
        <ViewPoButton fid={o.po_file_id} />
        {canDelete(o) && <Button variant="ghost" onClick={() => setDel(true)} className={btn.danger} data-testid="order-delete-btn"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>}
      </PageHeader>
      <DeleteOrderDialog order={delOpen || null} onOpenChange={(v) => !v && setDel(false)} onDone={() => nav("/orders")} />
      {o.hold_reason && editable && (
        <div className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="order-hold-reason">On hold — 940 not auto-released: {o.hold_reason}. Fix below and click Release 940.</div>
      )}
      <Header o={o} />
      <Lines o={o} items={items} edit={editable} map={map} setMap={setMap} ship={editable && !isCust ? ship : null} setShip={setShip} />
      {editable && (
        <Panel title={isCust ? "Fulfilment instructions for the 940 Warehouse Shipping Order" : "Ship replenishment to Group O"} testId="order-fulfilment-panel">
          <FulfilmentForm f={f} setF={setF} sites={sites} isCust={isCust} />
          {!isCust && (
            <div className="grid gap-4 px-5 pb-5 sm:grid-cols-4">
              <F label="BOL #"><Input value={shipMeta.bol} onChange={(e) => setShipMeta({ ...shipMeta, bol: e.target.value })} className={field} data-testid="ship-bol" /></F>
              <F label="Ship date"><Input type="date" value={shipMeta.ship_date} onChange={(e) => setShipMeta({ ...shipMeta, ship_date: e.target.value })} className={field} data-testid="ship-date" /></F>
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-800 px-5 py-3">
            <Button variant="ghost" onClick={() => run(() => api.post(`/orders/${id}/cancel`, { note: "Cancelled by user" }), "Order cancelled")} className={btn.danger} data-testid="order-cancel-btn"><XCircle className="mr-1.5 h-4 w-4" /> Cancel order</Button>
            <Button variant="ghost" onClick={save} className={btn.ghost} data-testid="order-save-btn"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
            {isCust ? (
              <Button onClick={submit} className={btn.primary} data-testid="order-submit-btn"><Send className="mr-1.5 h-4 w-4" /> Release 940</Button>
            ) : (
              <Button onClick={shipNow} className={btn.primary} data-testid="order-ship-btn"><Truck className="mr-1.5 h-4 w-4" /> Ship to xDock</Button>
            )}
          </div>
        </Panel>
      )}
      {["released", "short_shipped", "in_transit", "discrepancy"].includes(o.status) && (
        <div className="flex flex-wrap gap-2">
          {o.edi_940_id && <Button onClick={() => setView(o.edi_940_id)} className={btn.ghost} variant="ghost" data-testid="order-view-940-btn"><FileCode2 className="mr-1.5 h-4 w-4" /> View 940 Warehouse Shipping Order</Button>}
          <Button onClick={simulate} variant="ghost" className={btn.ghost} data-testid="order-simulate-response-btn">
            <FlaskConical className="mr-1.5 h-4 w-4" /> Simulate Group O {ediName(o.order_type === "customer" ? "945" : "861")} (test)
          </Button>
          <Button onClick={() => setImp({ open: true, raw: "" })} className={btn.primary} data-testid="order-import-response-btn">Import {ediName(o.order_type === "customer" ? "945" : "861")}</Button>
        </div>
      )}
      <Shipments o={o} />
      <OrderIdocPanel o={o} onChanged={load} />
      <History o={o} onView={setView} />
      <EdiImportDialog open={imp.open} initialRaw={imp.raw} onOpenChange={(v) => setImp({ ...imp, open: v })} onDone={load} />
      <RawEdiDialog open={!!view} messageId={view} onOpenChange={(v) => !v && setView(null)} onChanged={load} />
    </div>
  );
}
